import axios from "axios";
import axiosInstance from "utils/axiosInstance";
import { WarningNetworkFailure } from "helpers/alertsweet";

export const normalizeDonateApiUrl = (url: string) =>
  url.replace(/^http:\/\/api\.onetop\.la(?=\/|$)/i, "https://api.onetop.la");

const BASE_URL = normalizeDonateApiUrl(
  (
    process.env.REACT_APP_MAIN_API_URL ||
    process.env.REACT_APP_BASE_API_URL ||
    ""
  ).replace(/\/$/, ""),
);
const DONATE_URL = `${BASE_URL}/donate`;

export const DONATE_BUSINESS_ID =
  process.env.REACT_APP_DONATE_BUSINESS_ID || "";

const getAuthHeaders = (token: string, businessId?: string) => ({
  authorization: `Bearer ${token}`,
  ...(businessId ? { b: businessId } : {}),
});

const getQueryParams = (params: Record<string, any> = {}) => {
  const queryParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    queryParams.set(key, String(value));
  });

  return queryParams.toString();
};

export type DonateWidgetType =
  | "DONATION_ALERT"
  | "DONATION_GOAL"
  | "DONATION_LIST"
  | "CUSTOM";

export type DonateWidgetStatus = "ACTIVE" | "INACTIVE";

export type DonateWidgetSortBy = "createdAt" | "updatedAt" | "name" | "type";

export type DonateWidgetGridBreakpoint = "lg" | "md" | "sm" | "xs" | "xxs";

export type DonateWidgetGridLayoutItem = {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
  maxW?: number;
  maxH?: number;
  static?: boolean;
};

export type DonateWidgetGridItem = {
  id: string;
  type: DonateWidgetType | string;
  title: string;
  isVisible?: boolean;
  data?: Record<string, any>;
  settings?: Record<string, any>;
  styles?: Record<string, any>;
};

export type DonateWidgetGridLayout = {
  layouts?: Partial<
    Record<DonateWidgetGridBreakpoint, DonateWidgetGridLayoutItem[]>
  >;
  items: DonateWidgetGridItem[];
  breakpoints?: Record<DonateWidgetGridBreakpoint, number>;
  cols?: Record<DonateWidgetGridBreakpoint, number>;
  rowHeight?: number;
  margin?: [number, number];
  containerPadding?: [number, number];
  compactType?: "vertical" | "horizontal" | null;
  preventCollision?: boolean;
  isDraggable?: boolean;
  isResizable?: boolean;
};

export type DonateWidgetReactFlow = {
  nodes: any[];
  edges: any[];
  viewport?: {
    x: number;
    y: number;
    zoom: number;
    width?: number;
    height?: number;
    device?: "desktop" | "ipad" | "mobile" | string;
  };
};

export type DonateWidgetPayload = {
  name?: string;
  type?: DonateWidgetType;
  description?: string;
  isPublic?: boolean;
  isDefault?: boolean;
  status?: DonateWidgetStatus;
  gridLayout?: DonateWidgetGridLayout;
  reactFlow?: DonateWidgetReactFlow;
  settings?: Record<string, any>;
  styles?: Record<string, any>;
  metadata?: Record<string, any>;
};

export type DonateWidget = DonateWidgetPayload & {
  _id?: string;
  id?: string;
  publicKey?: string;
  widgetUrl?: string;
  donationAlertUrl?: string;
  donationAlertEventsUrl?: string;
  ttsAudioUrl?: string;
  notiAudioUrl?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type DonationAlertEvent = {
  id?: string;
  _id?: string;
  donationId?: string;
  name?: string;
  donorName?: string;
  donor?: string;
  amount?: number | string;
  currency?: string;
  message?: string;
  paymentMethod?: string;
  ttsAudioUrl?: string;
  notiAudioUrl?: string;
  [key: string]: any;
};

type DonateWidgetListParams = {
  token: string;
  businessId: string;
  skip?: number;
  limit?: number;
  type?: DonateWidgetType;
  status?: DonateWidgetStatus;
  isPublic?: boolean;
  isDefault?: boolean;
  keyword?: string;
  sortBy?: DonateWidgetSortBy;
  order?: "asc" | "desc";
};

export const donateErrorMessage = (error: any) => {
  if (!error) return "ເກີດຂໍ້ຜິດພາດ";
  if (error?.response?.data?.message) return error.response.data.message;
  if (error?.message) return error.message;
  return String(error);
};

export const donateWidgetCreateOne = async ({
  token,
  businessId,
  body,
}: {
  token: string;
  businessId: string;
  body: DonateWidgetPayload;
}): Promise<DonateWidget> => {
  try {
    const url = `${DONATE_URL}/widget/create-one`;
    const res = await axiosInstance.post(url, body, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("donateWidgetCreateOne error", error);
    throw error;
  }
};

export const donateWidgetGetMany = async ({
  token,
  businessId,
  skip = 0,
  limit = 50,
  type,
  status,
  isPublic,
  isDefault,
  keyword,
  sortBy,
  order,
}: DonateWidgetListParams): Promise<any> => {
  try {
    const queryParams = getQueryParams({
      skip,
      limit,
      type,
      status,
      isPublic,
      isDefault,
      keyword,
      sortBy,
      order,
    });
    const url = `${DONATE_URL}/widget/get-many${
      queryParams ? `?${queryParams}` : ""
    }`;
    const res = await axiosInstance.get(url, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("donateWidgetGetMany error", error);
    throw error;
  }
};

export const donateWidgetGetCount = async ({
  token,
  businessId,
  type,
  status,
  isPublic,
  isDefault,
  keyword,
}: Omit<
  DonateWidgetListParams,
  "skip" | "limit" | "sortBy" | "order"
>): Promise<any> => {
  try {
    const queryParams = getQueryParams({
      type,
      status,
      isPublic,
      isDefault,
      keyword,
    });
    const url = `${DONATE_URL}/widget/get-count${
      queryParams ? `?${queryParams}` : ""
    }`;
    const res = await axiosInstance.get(url, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("donateWidgetGetCount error", error);
    throw error;
  }
};

export const donateWidgetGetOne = async ({
  token,
  businessId,
  id,
}: {
  token: string;
  businessId: string;
  id: string;
}): Promise<DonateWidget> => {
  try {
    const url = `${DONATE_URL}/widget/get-one/${encodeURIComponent(id)}`;
    const res = await axiosInstance.get(url, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("donateWidgetGetOne error", error);
    throw error;
  }
};

export const donateWidgetUpdateOne = async ({
  token,
  businessId,
  id,
  body,
}: {
  token: string;
  businessId: string;
  id: string;
  body: DonateWidgetPayload;
}): Promise<DonateWidget> => {
  try {
    const url = `${DONATE_URL}/widget/update-one/${encodeURIComponent(id)}`;
    const res = await axiosInstance.put(url, body, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("donateWidgetUpdateOne error", error);
    throw error;
  }
};

export const donateWidgetDeleteOne = async ({
  token,
  businessId,
  id,
}: {
  token: string;
  businessId: string;
  id: string;
}): Promise<any> => {
  try {
    const url = `${DONATE_URL}/widget/delete-one/${encodeURIComponent(id)}`;
    const res = await axiosInstance.delete(url, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("donateWidgetDeleteOne error", error);
    throw error;
  }
};

export const donateWidgetPublicGetOne = async ({
  publicKey,
}: {
  publicKey: string;
}): Promise<DonateWidget> => {
  try {
    const url = `${DONATE_URL}/widget/public/get-one/${encodeURIComponent(
      publicKey,
    )}`;
    const res = await axios.get(url, {
      headers: {
        "Content-Type": "application/json",
      },
      timeout: 10000,
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("donateWidgetPublicGetOne error", error);
    throw error;
  }
};

export const getDonationAlertEventsUrl = ({
  publicKey,
}: {
  publicKey: string;
}) =>
  `${DONATE_URL}/widget/public/donation-alert/${encodeURIComponent(
    publicKey,
  )}/events`;

export const donateWidgetSendAlert = async ({
  token,
  businessId,
  userId,
  body,
}: {
  token: string;
  businessId: string;
  userId: string;
  body: Record<string, any>;
}): Promise<any> => {
  try {
    const url = `${DONATE_URL}/widget/alert/${encodeURIComponent(userId)}`;
    const res = await axiosInstance.post(url, body, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("donateWidgetSendAlert error", error);
    throw error;
  }
};

export const donateWidgetSetGoal = async ({
  token,
  businessId,
  userId,
  body,
}: {
  token: string;
  businessId: string;
  userId: string;
  body: Record<string, any>;
}): Promise<any> => {
  try {
    const url = `${DONATE_URL}/widget/goal/${encodeURIComponent(userId)}`;
    const res = await axiosInstance.post(url, body, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("donateWidgetSetGoal error", error);
    throw error;
  }
};

export const donateWidgetAddGoalAmount = async ({
  token,
  businessId,
  userId,
  body,
}: {
  token: string;
  businessId: string;
  userId: string;
  body: Record<string, any>;
}): Promise<any> => {
  try {
    const url = `${DONATE_URL}/widget/goal/${encodeURIComponent(userId)}/amount`;
    const res = await axiosInstance.post(url, body, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("donateWidgetAddGoalAmount error", error);
    throw error;
  }
};

export const donationGetMany = async ({
  findby = {},
  skip = 0,
  limit = 50,
  token,
  businessId,
}: {
  findby?: Record<string, any>;
  skip?: number;
  limit?: number;
  token: string;
  businessId: string;
}): Promise<any> => {
  try {
    const queryParams = getQueryParams({ ...findby, skip, limit });
    const url = `${DONATE_URL}/donation/get-many?${queryParams}`;
    const res = await axiosInstance.get(url, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("donationGetMany error", error);
    throw error;
  }
};

export const donationGetScreenHistory = async ({
  findby = {},
  skip = 0,
  limit = 20,
  token,
  businessId,
}: {
  findby?: Record<string, any>;
  skip?: number;
  limit?: number;
  token: string;
  businessId: string;
}): Promise<any> => {
  try {
    const queryParams = getQueryParams({ ...findby, skip, limit });
    const url = `${DONATE_URL}/donation/get-screen-history?${queryParams}`;
    const res = await axiosInstance.get(url, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("donationGetScreenHistory error", error);
    throw error;
  }
};

export const donationTermgameOrders = async ({
  findby = {},
  skip = 0,
  limit = 50,
  token,
  businessId,
}: {
  findby?: Record<string, any>;
  skip?: number;
  limit?: number;
  token: string;
  businessId: string;
}): Promise<any> => {
  try {
    const queryParams = getQueryParams({ ...findby, skip, limit });
    const url = `${DONATE_URL}/donation/termgame/orders${
      queryParams ? `?${queryParams}` : ""
    }`;
    const res = await axiosInstance.get(url, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("donationTermgameOrders error", error);
    throw error;
  }
};

type WalletTransactionType =
  | "DONATION"
  | "WITHDRAWAL"
  | "ADJUSTMENT"
  | "TERMGAME_INCOME";

type WalletTransactionStatus =
  | "PENDING"
  | "SUCCESS"
  | "FAILED"
  | "CANCELED";

export const walletTransactionGetMany = async ({
  token,
  businessId,
  type,
  status,
  dateFrom,
  dateTo,
  skip = 0,
  limit = 50,
  sortBy = "createdAt",
  order = "desc",
}: {
  token: string;
  businessId: string;
  type?: WalletTransactionType;
  status?: WalletTransactionStatus;
  dateFrom?: string;
  dateTo?: string;
  skip?: number;
  limit?: number;
  sortBy?: string;
  order?: "desc" | "asc";
}): Promise<any> => {
  try {
    const safeSkip = Math.max(Number(skip) || 0, 0);
    const safeLimit = Math.min(Math.max(Number(limit) || 50, 1), 100);
    const safeOrder = order === "asc" ? "asc" : "desc";

    const queryParams = getQueryParams({
      type,
      status,
      dateFrom,
      dateTo,
      skip: safeSkip,
      limit: safeLimit,
      sortBy,
      order: safeOrder,
    });

    const url = `${DONATE_URL}/wallet/transaction/get-many${
      queryParams ? `?${queryParams}` : ""
    }`;
    const res = await axiosInstance.get(url, {
      headers: getAuthHeaders(token, businessId),
    });

    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("walletTransactionGetMany error", error);
    throw error;
  }
};

export const walletTransactionGetCount = async ({
  token,
  businessId,
  type,
  status,
  dateFrom,
  dateTo,
}: {
  token: string;
  businessId: string;
  type?: WalletTransactionType;
  status?: WalletTransactionStatus;
  dateFrom?: string;
  dateTo?: string;
}): Promise<any> => {
  try {
    const queryParams = getQueryParams({ type, status, dateFrom, dateTo });
    const url = `${DONATE_URL}/wallet/transaction/get-count${
      queryParams ? `?${queryParams}` : ""
    }`;
    const res = await axiosInstance.get(url, {
      headers: getAuthHeaders(token, businessId),
    });

    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("walletTransactionGetCount error", error);
    throw error;
  }
};

export const donationShowOnScreen = async ({
  id,
  token,
  businessId,
}: {
  id: string;
  token: string;
  businessId: string;
}): Promise<any> => {
  try {
    const url = `${DONATE_URL}/donation/show-on-screen/${encodeURIComponent(
      id,
    )}`;
    const res = await axiosInstance.post(
      url,
      {},
      {
        headers: getAuthHeaders(token, businessId),
      },
    );
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("donationShowOnScreen error", error);
    throw error;
  }
};

export const getDonationAlertObsUrl = (username = "") => {
  return `https://donate.onetop.la/${username}`;
};

type DashboardStatus = "SUCCESS" | "IN_QUEUE" | "CREATED" | "FAILED";

export type DonateDashboardSummaryResponse = {
  total: number;
  success: number;
  inQueue: number;
  created: number;
  failed: number;
};

export type DonateDashboardDailyAmountItem = {
  date: string;
  donateAmount: number;
  topupAmount: number;
};

export type DonateDashboardRecentTransaction = {
  _id: string;
  status: DashboardStatus;
  total: number;
  createdAt: string;
  updatedAt?: string;
  type?: "DONATE" | "TOPUP";
  orders?: Array<{
    productAgentName?: string;
    productAgentPackageRef?: {
      packageGame?: string;
    };
  }>;
};

export type DonateDashboardRecentTransactionsResponse = {
  data: DonateDashboardRecentTransaction[];
  count: number;
  skip: number;
  limit: number;
};

type DonateDashboardSummaryParams = {
  token: string;
  businessId: string;
  status?: DashboardStatus;
  dateFrom?: string;
  dateTo?: string;
  timezone?: string;
};

type DonateDashboardDailyAmountParams = {
  token: string;
  businessId: string;
  dateFrom?: string;
  dateTo?: string;
  timezone?: string;
  status?: DashboardStatus;
};

type DonateDashboardRecentTransactionsParams = {
  token: string;
  businessId: string;
  skip?: number;
  limit?: number;
  sortBy?: "createdAt" | "updatedAt" | "total" | "status";
  order?: "asc" | "desc";
  type?: "DONATE" | "TOPUP";
  status?: DashboardStatus;
  dateFrom?: string;
  dateTo?: string;
  timezone?: string;
};

export type DonateReferralStatus =
  | "pending"
  | "valid"
  | "invalid"
  | "self_referral"
  | "rewarded"
  | "rejected";

export type DonateReferralSummary = {
  totalReferredUsers?: number;
  uniqueReferrers?: number;
  pendingReviewCount?: number;
  validCount?: number;
  invalidCount?: number;
  selfReferralCount?: number;
  rewardedCount?: number;
  rejectedCount?: number;
  totalRewardAmount?: number | string;
  currency?: string;
};

export type DonateReferralListItem = {
  _id?: string;
  referredUser?: {
    _id?: string;
    name?: string;
    username?: string;
    email?: string;
    phone?: string;
    affiliateCode?: string;
    referralCode?: string;
    createdAt?: string;
  };
  referrerUser?: {
    _id?: string;
    name?: string;
    username?: string;
    email?: string;
    phone?: string;
    affiliateCode?: string;
    referralCode?: string;
  };
  submittedReferrer?: string;
  normalizedReferrer?: string;
  status?: DonateReferralStatus | string;
  reward?: {
    amount?: number | string;
    currency?: string;
    ratePercent?: number | string;
    baseAmount?: number | string;
    eligibleEvent?: string;
    payoutState?: string;
  };
  createdAt?: string;
  updatedAt?: string;
};

type DonateReferralQueryParams = {
  token: string;
  businessId: string;
  skip?: number;
  limit?: number;
  search?: string;
  status?: DonateReferralStatus | string;
  dateFrom?: string;
  dateTo?: string;
};

export const donateDashboardGetSummary = async ({
  token,
  businessId,
  status,
  dateFrom,
  dateTo,
  timezone,
}: DonateDashboardSummaryParams): Promise<DonateDashboardSummaryResponse> => {
  try {
    const queryParams = getQueryParams({ status, dateFrom, dateTo, timezone });
    const url = `${DONATE_URL}/dashboard/get-summary${
      queryParams ? `?${queryParams}` : ""
    }`;
    const res = await axiosInstance.get(url, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("donateDashboardGetSummary error", error);
    throw error;
  }
};

export const donateReferralGetPolicy = async ({
  token,
  businessId,
}: {
  token: string;
  businessId?: string;
}): Promise<any> => {
  try {
    const url = `${DONATE_URL}/referral/policy`;
    const res = await axiosInstance.get(url, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("donateReferralGetPolicy error", error);
    throw error;
  }
};

export const donateReferralGetSummary = async ({
  token,
  businessId,
  search,
  status,
  dateFrom,
  dateTo,
}: DonateReferralQueryParams): Promise<any> => {
  try {
    const queryParams = getQueryParams({
      search,
      status,
      dateFrom,
      dateTo,
    });
    const url = `${DONATE_URL}/referral/summary${
      queryParams ? `?${queryParams}` : ""
    }`;
    const res = await axiosInstance.get(url, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("donateReferralGetSummary error", error);
    throw error;
  }
};

export const donateReferralGetList = async ({
  token,
  businessId,
  skip = 0,
  limit = 10,
  search,
  status,
  dateFrom,
  dateTo,
}: DonateReferralQueryParams): Promise<any> => {
  try {
    const queryParams = getQueryParams({
      skip,
      limit,
      search,
      status,
      dateFrom,
      dateTo,
    });
    const url = `${DONATE_URL}/referral/list${
      queryParams ? `?${queryParams}` : ""
    }`;
    const res = await axiosInstance.get(url, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("donateReferralGetList error", error);
    throw error;
  }
};

export const donationGetPolicy = async ({
  token,
  businessId,
}: {
  token: string;
  businessId?: string;
}): Promise<any> => {
  try {
    const url = `${DONATE_URL}/donations/policy`;
    const res = await axiosInstance.get(url, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("donationGetPolicy error", error);
    throw error;
  }
};

export const donateDashboardGetDailyAmount = async ({
  token,
  businessId,
  dateFrom,
  dateTo,
  timezone = "Asia/Vientiane",
  status,
}: DonateDashboardDailyAmountParams): Promise<DonateDashboardDailyAmountItem[]> => {
  try {
    const queryParams = getQueryParams({ dateFrom, dateTo, timezone, status });
    const url = `${DONATE_URL}/dashboard/get-daily-amount${
      queryParams ? `?${queryParams}` : ""
    }`;
    const res = await axiosInstance.get(url, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("donateDashboardGetDailyAmount error", error);
    throw error;
  }
};

export const donateDashboardGetRecentTransactions = async ({
  token,
  businessId,
  skip = 0,
  limit = 8,
  sortBy = "createdAt",
  order = "desc",
  type,
  status,
  dateFrom,
  dateTo,
  timezone = "Asia/Vientiane",
}: DonateDashboardRecentTransactionsParams): Promise<DonateDashboardRecentTransactionsResponse> => {
  try {
    const safeSkip = Math.max(Number(skip) || 0, 0);
    const safeLimit = Math.min(Math.max(Number(limit) || 8, 1), 100);
    const safeOrder = order === "asc" ? "asc" : "desc";

    const queryParams = getQueryParams({
      skip: safeSkip,
      limit: safeLimit,
      sortBy,
      order: safeOrder,
      type,
      status,
      dateFrom,
      dateTo,
      timezone,
    });

    const url = `${DONATE_URL}/dashboard/get-recent-transactions${
      queryParams ? `?${queryParams}` : ""
    }`;
    const res = await axiosInstance.get(url, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") WarningNetworkFailure();
    console.error("donateDashboardGetRecentTransactions error", error);
    throw error;
  }
};

export const getDonationGoalObsUrl = ({
  userId = "",
  title = "",
  current = 0,
  target = 0,
}: {
  userId?: string;
  title?: string;
  current?: string | number;
  target?: string | number;
}) => {
  const query = new URLSearchParams({
    userId,
    title,
    current: String(current || 0),
    target: String(target || 0),
  }).toString();
  return `${window.location.origin}/donate/obs/goal?${query}`;
};
